import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { RouteLoader } from '@/components/RouteLoader';
import { analytics } from '@/lib/analytics';
import { clearPendingDestination } from '@/lib/auth-next';

export function AuthCallback() {
  const navigate = useNavigate();
  // Supabase fires SIGNED_IN once on mount + occasionally on token refresh.
  // We must NEVER alias() twice for the same login — that would re-merge
  // identities. Module-ref guard pairs with the server's analytics_signed_up_at
  // marker (server-side first-claim) for a belt-and-braces guarantee.
  const stitched = useRef(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event !== 'SIGNED_IN' || !session) return;
      const userId = session.user?.id;
      if (!userId) return;

      // post-callback returns { is_new_account, redirect_to } — the
      // redirect_to is the server-verified destination from the auth_next
      // cookie (S3.4). is_new_account drives the default fallback: a
      // brand-new account goes STRAIGHT to /onboarding rather than
      // bouncing through /saved (which would flash the saved-page
      // skeleton before ProtectedRoute kicked it to /onboarding anyway).
      let serverRedirectTo: string | null = null;
      let isNewAccount = false;
      if (!stitched.current) {
        stitched.current = true;
        try {
          const result = await runIdentityStitch(session.access_token, userId, session.user?.email);
          serverRedirectTo = result.redirectTo;
          isNewAccount = result.isNewAccount;
        } catch (err) {
          // Identity failure shouldn't block the user from reaching their app.
          // The next signin will retry the server marker; alias() either way
          // is now skipped for this distinct_id pair.
          console.warn('[auth/callback] identity stitch failed', err);
        }
      }

      // Sweep the sessionStorage marker — server has consumed the cookie,
      // any leftover client-side state is stale.
      clearPendingDestination();

      const pendingExtensionId = localStorage.getItem('pendingExtensionAuth');
      if (pendingExtensionId) {
        localStorage.removeItem('pendingExtensionAuth');
        navigate(`/extension-auth?extensionId=${pendingExtensionId}`, { replace: true });
        return;
      }
      if (serverRedirectTo) {
        navigate(serverRedirectTo, { replace: true });
        return;
      }
      // No explicit destination from the auth_next cookie. Route by account
      // age so a brand-new signup lands DIRECTLY on /onboarding without
      // the /saved → ProtectedRoute → /onboarding flash.
      navigate(isNewAccount ? '/onboarding' : '/saved', { replace: true });
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  // Neutral spinner during the brief identity-stitch + redirect window.
  // We deliberately avoid the /saved skeleton here so a brand-new signup
  // (which is about to be redirected to /onboarding) doesn't see the
  // saved-pages layout paint for a beat.
  return <RouteLoader />;
}

interface StitchResult {
  redirectTo: string | null;
  isNewAccount: boolean;
}

async function runIdentityStitch(accessToken: string, userId: string, email?: string): Promise<StitchResult> {
  // Pass the client's first-touch UTM snapshot in the body so the server
  // can persist it to user_profiles.attribution on a fresh signup (S3.9).
  // This becomes the ladder step 4-bis for attribution lookups on events
  // that don't have a subscription_id (trial-end downgrade, account
  // deletion of a free user).
  const earlyFirstTouch = analytics.getFirstTouch();
  const attributionBody = {
    first_utm_source: earlyFirstTouch.first_utm_source,
    first_utm_medium: earlyFirstTouch.first_utm_medium,
    first_utm_campaign: earlyFirstTouch.first_utm_campaign,
    first_utm_content: earlyFirstTouch.first_utm_content,
    first_utm_term: earlyFirstTouch.first_utm_term,
    first_landing_url: earlyFirstTouch.first_landing_url,
    first_referrer: earlyFirstTouch.first_referrer,
    first_landing_at: earlyFirstTouch.first_landing_at,
  };
  const res = await fetch('/api/auth/post-callback', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin', // send the auth_next cookie
    body: JSON.stringify(attributionBody),
  });
  if (!res.ok) throw new Error(`post-callback ${res.status}`);
  const data = (await res.json()) as { is_new_account: boolean; email?: string | null; redirect_to?: string | null };

  const emailDomain = (email || data.email || '').split('@')[1] || undefined;
  const firstTouch = analytics.getFirstTouch();
  const lastTouch = analytics.getLastTouch();
  const setProps: Record<string, unknown> = {
    email_domain: emailDomain,
    last_utm_source: lastTouch.last_utm_source,
    last_utm_medium: lastTouch.last_utm_medium,
    last_utm_campaign: lastTouch.last_utm_campaign,
    last_utm_content: lastTouch.last_utm_content,
  };

  if (data.is_new_account) {
    // STRICT order per plan §1.5: alias → identify → signup_completed.
    analytics.alias(userId);
    const setOnce: Record<string, unknown> = {
      first_utm_source: firstTouch.first_utm_source,
      first_utm_medium: firstTouch.first_utm_medium,
      first_utm_campaign: firstTouch.first_utm_campaign,
      first_utm_content: firstTouch.first_utm_content,
      first_utm_term: firstTouch.first_utm_term,
      first_landing_url: firstTouch.first_landing_url,
      first_referrer: firstTouch.first_referrer,
      first_landing_at: firstTouch.first_landing_at,
      signup_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      signup_at: new Date().toISOString(),
    };
    analytics.identify(userId, { set: setProps, setOnce });

    const timeToSignupSeconds = firstTouch.first_landing_at
      ? Math.max(0, Math.round((Date.now() - new Date(firstTouch.first_landing_at).getTime()) / 1000))
      : undefined;
    analytics.track('signup_completed', {
      method: 'google',
      user_id: userId,
      email_domain: emailDomain,
      first_utm_source: firstTouch.first_utm_source,
      first_utm_medium: firstTouch.first_utm_medium,
      first_utm_campaign: firstTouch.first_utm_campaign,
      first_utm_content: firstTouch.first_utm_content,
      signup_landing_url: firstTouch.first_landing_url,
      signup_referrer: firstTouch.first_referrer,
      signup_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      time_to_signup_seconds: timeToSignupSeconds,
      is_new_account: true,
    });
  } else {
    // Returning user: identify only (NEVER alias — would corrupt identity graph).
    analytics.identify(userId, { set: setProps });
    analytics.track('signin_completed', { method: 'google', user_id: userId });
  }

  return {
    redirectTo: typeof data.redirect_to === 'string' && data.redirect_to.length > 0 ? data.redirect_to : null,
    isNewAccount: !!data.is_new_account,
  };
}
